import { FC } from "react";

import { COMMUNICATION_PREFERENCE_QUESTION_TITLE_MAX_LENGTH, Locales, Nullable } from "@simplyk/common";
import { useFormContext } from "react-hook-form";

import { getTranslationLabelLanguage } from "../helpers/loadTranslationLabels";

import { TranslatedTextField } from "./TranslatedTextField";
import { TranslationSection } from "./TranslationSection";

import { isRequired } from "@/common/helpers/validators";
import { useTranslate } from "@/common/hooks/useTranslate";
import { TranslationLine } from "@/components/TranslationLine/TranslationLine";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";

interface CommunicationPreferencesTitleTranslationProps {
  questionTitle: Nullable<string>;
  savedTranslation: Nullable<string> | undefined;
  formLocale: Locales;
  translationLocale: Locales;
}

// Only a custom title needs translating, and its main language is edited with the question, so it only reads here.
export const CommunicationPreferencesTitleTranslation: FC<CommunicationPreferencesTitleTranslationProps> = ({
  questionTitle,
  savedTranslation,
  formLocale,
  translationLocale,
}) => {
  const { t } = useTranslate();
  const { control, formState, setValue } = useFormContext<{ communicationPreferencesTitle?: string }>();
  const error = formState.errors.communicationPreferencesTitle;

  if (!questionTitle) {
    return null;
  }

  return (
    <TranslationSection title={t("dashboard", "communicationPreferences.questionTitle")}>
      <TranslationLine>
        <DashboardTextField
          label={t("dashboard", "communicationPreferences.questionTitleLabel", {
            lng: getTranslationLabelLanguage(formLocale),
          })}
          value={questionTitle}
          disabled
        />
        <TranslatedTextField
          fieldName="communicationPreferencesTitle"
          defaultFieldValue={questionTitle}
          defaultTranslationFieldValue={savedTranslation}
          setValue={setValue}
          translationLocale={translationLocale}
          label={t("dashboard", "communicationPreferences.questionTitleLabel", {
            lng: getTranslationLabelLanguage(translationLocale),
          })}
          inputProps={{ maxLength: COMMUNICATION_PREFERENCE_QUESTION_TITLE_MAX_LENGTH }}
          rules={[isRequired(t("dashboard", "common.isRequired"))]}
          control={control}
          error={Boolean(error)}
          errorMessage={error?.message}
          dataTest="communication-preferences-title-translation"
        />
      </TranslationLine>
    </TranslationSection>
  );
};
